import React, { useEffect, useState } from "react";
import axios from "axios";
import Nav from "../components/sections/Nav";
import Footer from "../components/sections/Footer";
import Seo from "../components/Seo";
import RevealOnScroll from "../components/RevealOnScroll";
import SectionDivider from "../components/SectionDivider";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (iso) => {
    try {
        return new Date(iso).toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
        });
    } catch {
        return "";
    }
};

const CATEGORIES = [
    "All",
    "EMDR",
    "Anxiety",
    "Healing",
    "Relationships",
    "Trauma",
    "Self-Doubt",
    "ACT",
    "Brainspotting",
    "DBT",
    "Therapy",
];

const FilterPill = ({ label, active, onClick }) => (
    <button
        onClick={onClick}
        data-testid={`insights-filter-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
        data-active={active}
        className="transition-all duration-300"
        style={{
            fontFamily: "'Figtree', sans-serif",
            fontSize: "10px",
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            fontWeight: 500,
            padding: "10px 18px",
            borderRadius: 0,
            border: "1px solid",
            borderColor: active ? "#9B7D5A" : "#E2DDD8",
            background: active ? "#9B7D5A" : "transparent",
            color: active ? "#FAFAF8" : "#8A7968",
            cursor: "pointer",
        }}
        onMouseEnter={(e) => {
            if (!active) {
                e.currentTarget.style.borderColor = "#9B7D5A";
                e.currentTarget.style.color = "#2C2825";
            }
        }}
        onMouseLeave={(e) => {
            if (!active) {
                e.currentTarget.style.borderColor = "#E2DDD8";
                e.currentTarget.style.color = "#8A7968";
            }
        }}
    >
        {label}
    </button>
);

const Hero = () => (
    <section
        data-testid="insights-hero"
        className="relative pt-44 pb-28 lg:pt-52 lg:pb-32"
        style={{ background: "#EEF2ED" }}
    >
        <div className="mx-auto max-w-[900px] px-6 lg:px-12 text-center" data-reveal>
            <span className="editorial-eyebrow">Alchemy Insights</span>
            <h1 className="mt-6 font-serif text-5xl sm:text-6xl lg:text-[68px] leading-[1.05] text-charcoal text-balance">
                Reflections on healing, growth, and the work of{" "}
                <em className="italic text-gold">becoming.</em>
            </h1>
            <div className="mx-auto mt-8" style={{ height: 2, width: 64, background: "#8A9E85" }} />
            <p className="mt-8 max-w-2xl mx-auto text-lg sm:text-xl leading-[1.85] text-charcoal-soft text-pretty">
                Slow, considered writing — for the people doing this work.
            </p>
        </div>
    </section>
);

const PostCard = ({ post }) => {
    const [hover, setHover] = useState(false);
    return (
        <a
            href={`/alchemy-insights/${post.slug}`}
            data-testid={`insights-card-${post.slug}`}
            className="group flex flex-col h-full transition-all duration-300"
            style={{
                background: "#FAFAF8",
                border: "1px solid #E2DDD8",
                borderTop: hover ? "2px solid #9B7D5A" : "1px solid #E2DDD8",
                borderRadius: 0,
                marginTop: hover ? "-1px" : 0,
            }}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
        >
            {post.featured_image_url ? (
                <div className="aspect-[4/3] overflow-hidden">
                    <img
                        src={post.featured_image_url}
                        alt={post.title}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                    />
                </div>
            ) : (
                <div
                    className="aspect-[4/3] flex items-center justify-center"
                    style={{ background: "#F2EDE6" }}
                    aria-hidden
                >
                    <span className="font-serif text-5xl" style={{ color: "#9B7D5A" }}>✦</span>
                </div>
            )}
            <div className="p-8 lg:p-10 flex flex-col flex-1">
                <p
                    className="font-medium uppercase"
                    style={{
                        fontFamily: "'Figtree', sans-serif",
                        fontSize: "10px",
                        letterSpacing: "0.2em",
                        color: "#9B7D5A",
                    }}
                    data-testid={`insights-card-category-${post.slug}`}
                >
                    {post.category || (post.tags && post.tags[0]) || ""}
                </p>
                <h3 className="mt-4 font-serif text-charcoal leading-[1.15]" style={{ fontSize: "28px" }}>
                    {post.title}
                </h3>
                <div className="my-5" style={{ height: 1, width: 36, background: "#8A9E85" }} />
                <p className="text-base leading-[1.85] text-charcoal-soft flex-1">{post.excerpt}</p>
                <span
                    className="mt-8 text-[11px] tracking-[0.28em] uppercase font-medium gold-link self-start"
                    style={{ color: "#9B7D5A" }}
                >
                    Read More →
                </span>
            </div>
        </a>
    );
};

const Placeholder = () => (
    <div className="py-20 text-center" data-testid="insights-empty">
        <span className="font-serif text-4xl" style={{ color: "#9B7D5A" }}>✦</span>
        <p
            className="mt-6 font-serif italic"
            style={{ fontSize: "22px", color: "#8A7968", lineHeight: 1.55 }}
        >
            The first reflection is being written. Come back soon.
        </p>
    </div>
);

export default function AlchemyInsightsPage() {
    const [posts, setPosts] = useState(null);
    const [activeCategory, setActiveCategory] = useState("All");

    useEffect(() => {
        let alive = true;
        axios
            .get(`${API}/blog`)
            .then((r) => { if (alive) setPosts(r.data); })
            .catch(() => { if (alive) setPosts([]); });
        return () => { alive = false; };
    }, []);

    const filtered = posts === null
        ? null
        : activeCategory === "All"
            ? posts
            : posts.filter((p) => (p.category || (p.tags && p.tags[0])) === activeCategory);

    return (
        <main className="bg-ivory text-charcoal" data-testid="alchemy-insights-page">
            <Seo
                title="Alchemy Insights | Reflections from Alchemy Practice"
                description="Slow, considered writing on healing, growth, and the work of becoming — from Austine, LPC at Alchemy Practice."
            />
            <Nav />
            <RevealOnScroll>
                <Hero />
                <SectionDivider color="gold" bg="#EEF2ED" />
                <section
                    data-testid="insights-grid-section"
                    className="py-24 lg:py-32"
                    style={{ background: "#FAFAF8" }}
                >
                    <div className="mx-auto max-w-[1300px] px-6 lg:px-12">
                        {posts !== null && posts.length > 0 && (
                            <div
                                className="flex flex-wrap items-center justify-center gap-2.5 mb-16 lg:mb-20"
                                data-testid="insights-filter-strip"
                                data-reveal
                            >
                                {CATEGORIES.map((cat) => (
                                    <FilterPill
                                        key={cat}
                                        label={cat}
                                        active={activeCategory === cat}
                                        onClick={() => setActiveCategory(cat)}
                                    />
                                ))}
                            </div>
                        )}

                        {posts === null ? (
                            <p className="text-center text-charcoal-soft">Loading…</p>
                        ) : posts.length === 0 ? (
                            <Placeholder />
                        ) : filtered.length === 0 ? (
                            <p
                                className="text-center font-serif italic py-16"
                                style={{ fontSize: "20px", color: "#8A7968" }}
                                data-testid="insights-empty-filter"
                            >
                                No reflections in this category yet.
                            </p>
                        ) : (
                            <div
                                className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10"
                                data-testid="insights-grid"
                                data-reveal
                            >
                                {filtered.map((p) => <PostCard key={p.id} post={p} />)}
                            </div>
                        )}
                    </div>
                </section>
                <Footer />
            </RevealOnScroll>
        </main>
    );
}
